import React, { useState, useEffect } from 'react'
import { CircularProgress, Typography, withStyles } from '@material-ui/core'
import styles from './styles'
import dataFunc, { settingItemListTypeEnum, settingItemTypeEnum } from './data'
import clsx from 'clsx'
import { track } from '@amplitude/analytics-browser'
import { hasYoutubeAttachment } from '../../utils/attachment'
import {
  CoreAutoComplete,
  CoreButton,
  CoreIconButton,
} from 'src/components/core'
import { Icon } from '@iconify/react'
import ImageSettings from './ImageSettings'

const Settings = props => {
  const {
    classes,
    settingsChangeHandler,
    attachments = [],
    documentFollowing,
  } = props

  const [settingList, setSettingList] = useState([])
  const [loading, setLoading] = useState(false)
  const [settingsAccordionOpen, setSettingsAccordionOpen] = useState(true)

  const [selectedSettings, setSelectedSettings] = useState([])

  useEffect(() => {
    settingsChangeHandler(selectedSettings)
  }, [selectedSettings])

  // Keep imageSource aligned with available sources.
  useEffect(() => {
    const hasYoutube = hasYoutubeAttachment(attachments)

    setSelectedSettings(prev => {
      const idx = prev.findIndex(s => s.key === 'imageSource')
      if (idx === -1) return prev

      const current = prev[idx].value
      const desired =
        hasYoutube && current !== 'source' && current !== 'none'
          ? 'source'
          : !hasYoutube && current === 'source'
          ? 'imageGen'
          : null

      if (!desired) return prev

      const next = [...prev]
      next[idx] = { ...next[idx], value: desired }
      return next
    })
  }, [attachments])

  // Remove tone and audience settings when they should be hidden
  useEffect(() => {
    if (shouldHideToneAndAudience()) {
      // Remove tone and audience settings from settingList
      setSettingList(prevList =>
        prevList.filter(
          item => item.type !== 'tone' && item.type !== 'audience',
        ),
      )

      // Remove tone and audience settings from selectedSettings
      setSelectedSettings(prev =>
        prev.filter(item => item.key !== 'tone' && item.key !== 'audience'),
      )
    }
  }, [attachments.length, documentFollowing])

  const handleSettingsAccordionToggle = () => {
    setSettingsAccordionOpen(!settingsAccordionOpen)
  }

  const handleClickOnSettingAddButton = async _category => {
    if (!_category) {
      console.error('Category is undefined or null')
      return
    }

    try {
      const category = { ..._category } // Clone the category object to ensure immutability

      // Track the action
      track('creation_setting_selected', { creation_setting: category.title })

      // Dynamically execute listFunc if the listType is DYNAMIC
      if (
        category.settingItems &&
        category.settingItems[0] &&
        category.settingItems[0].listType === settingItemListTypeEnum.DYNAMIC &&
        typeof category.settingItems[0].listFunc === 'function'
      ) {
        setLoading(true)
        // Fetch the dynamic list and resolve the Promise
        const list = await category.settingItems[0].listFunc()

        // Update the first item immutably
        category.settingItems = category.settingItems.map((item, index) =>
          index === 0 ? { ...item, list } : item,
        )
        setLoading(false)
      }

      // Update state immutably with the resolved category
      setSettingList(prevList => [...prevList, category])

      // Automatically add default values to selectedSettings for all settings with defaults
      const defaultSettings = category.settingItems
        .filter(
          item => item.defaultValue && item.defaultValue.value !== undefined,
        )
        .map(item => ({
          key: item.key,
          value:
            item.key === 'imageSource' && hasYoutubeAttachment(attachments)
              ? 'source'
              : item.defaultValue.value,
        }))

      if (defaultSettings.length > 0) {
        setSelectedSettings(prev => {
          // Filter out any existing settings with the same keys to avoid duplicates
          const existingKeys = prev.map(s => s.key)
          const newDefaults = defaultSettings.filter(
            s => !existingKeys.includes(s.key),
          )
          return [...prev, ...newDefaults]
        })
      }
    } catch (error) {
      console.error('Error handling setting addition:', error)
    }
  }

  const removeItem = item => {
    let newSettingList = settingList

    if (item && item.removeItems) {
      // Remove items from the setting list
      newSettingList = settingList.map(items => {
        return {
          ...items,
          settingItems: items.settingItems.filter(
            settingItem => !item.removeItems.includes(settingItem.key),
          ),
        }
      })
    }

    newSettingList = newSettingList.map(setting => {
      return {
        ...setting,
        settingItems: setting.settingItems.filter(x => x.key != item.key),
      }
    })

    newSettingList = newSettingList.filter(x => x.settingItems.length > 0)

    setSettingList(newSettingList)

    // Remove all related settings from selectedSettings
    let newSelectedSettings = selectedSettings
    if (item.removeItems) {
      // Remove all items in the group
      newSelectedSettings = selectedSettings.filter(
        x => !item.removeItems.includes(x.key),
      )
    } else {
      // Remove single item
      newSelectedSettings = selectedSettings.filter(x => x.key != item.key)
    }
    setSelectedSettings(newSelectedSettings)
  }

  // Check if tone and audience settings should be hidden
  const shouldHideToneAndAudience = () => {
    return attachments.length > 0 && documentFollowing === 'preserve'
  }

  const addItemsToSettingList = (settingList, addItemsKeys, data) => {
    // Loop through addItemsKeys to find the corresponding settingItems
    addItemsKeys.forEach(keyToAdd => {
      data.forEach(section => {
        section.settingItems.forEach(settingItem => {
          if (settingItem.key === keyToAdd) {
            // Check if the item is already in the settingList to avoid duplicates
            const isAlreadyAdded = settingList.some(existingSection =>
              existingSection.settingItems.some(
                existingItem => existingItem.key === settingItem.key,
              ),
            )

            if (!isAlreadyAdded) {
              // Find or create the section in the settingList
              let sectionInSettingList = settingList.find(
                existingSection => existingSection.type === section.type,
              )
              if (!sectionInSettingList) {
                sectionInSettingList = {
                  type: section.type,
                  title: section.title,
                  settingItems: [],
                }
                settingList.push(sectionInSettingList)
              }

              // Add the item to the section
              sectionInSettingList.settingItems.push(settingItem)
            }
          }
        })
      })
    })

    return settingList
  }

  const onChangeHandler = (item, value) => {
    let newSelectedSettings = selectedSettings.filter(x => x.key != item.key)

    if (item.key === 'imageSource') {
      if (value === 'none' || value === 'source') {
        // Hide all image-related settings when "No Images" or "Source" is selected
        const imageSettingKeys = [
          'imageStyle',
          'imageCharacter',
          'imageGenerationModel',
        ]
        newSelectedSettings = newSelectedSettings.filter(
          x => !imageSettingKeys.includes(x.key),
        )
      }
    }

    // Ensure quiz and survey counts are always numbers
    let processedValue = value
    if (item.key === 'finalQuizCount' || item.key === 'finalSurveyCount') {
      processedValue = parseInt(value, 10)
      // Handle NaN case - fallback to 0
      if (isNaN(processedValue)) {
        processedValue = 0
      }
    }

    let newSetting = { key: item.key, value: processedValue }

    // Model selection is no longer exposed; restore the default when images are turned back on
    if (item.key === 'imageSource' && value === 'imageGen') {
      newSelectedSettings = [
        ...newSelectedSettings.filter(x => x.key !== 'imageGenerationModel'),
        { key: 'imageGenerationModel', value: 'gpt-image-2.5' },
      ]
    }

    setSelectedSettings([...newSelectedSettings, newSetting])
  }

  const renderSettingItem = (item, isGrouped = false, groupTitle = '') => {
    let list = item.list
    return (
      <div key={item.key} className={classes.root_content}>
        <div className={classes.root_content_text}>
          <div className={classes.setting_header}>
            <Typography className={classes.setting_title}>
              {item.title}
            </Typography>
            {!isGrouped && (
              <CoreIconButton
                color="dark"
                size="small"
                variant="text"
                onClick={() => removeItem(item)}
                className={classes.delete_button}
              >
                <Icon icon="iconamoon:close-light" />
              </CoreIconButton>
            )}
          </div>
          <Typography className={classes.root_content_text_description}>
            {item?.description}
          </Typography>
          <div className={classes.auto_complete}>
            <CoreAutoComplete
              marginTop={false}
              placeholder={item.placeholder}
              title={item.description}
              defaultValue={item.defaultValue}
              freeSolo={item.freeSolo == false ? false : true}
              selectOnFocus
              multiline
              options={list}
              disableClearable={true}
              onChange={(event, value) => {
                onChangeHandler(
                  item,
                  value != null ? value.value : event.target.value,
                )
              }}
              onBlur={(event, value) => {
                if (list.find(x => x.name == event.target.value) == undefined) {
                  onChangeHandler(
                    item,
                    value != null ? value.value : event.target.value,
                  )
                }
              }}
              id={'auto_complete_' + item.key}
              key={'auto_complete_' + item.key}
              inputProps={{
                style: {
                  multiline: true,
                },
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderGroupedSettings = category => {
    // Check if imageSource is set to none
    const imageSourceSetting = selectedSettings.find(
      s => s.key === 'imageSource',
    )
    const shouldShowImageSettings = imageSourceSetting
      ? imageSourceSetting.value !== 'none'
      : true

    return (
      <div key={category.type} className={classes.groupedSettingsContainer}>
        <div className={classes.groupedSettingsHeader}>
          <div className={classes.groupedSettingsHeaderTitleContainer}>
            <Typography className={classes.groupedSettingsTitle}>
              {category.title}
            </Typography>
            <CoreIconButton
              color="dark"
              size="small"
              variant="text"
              onClick={() =>
                removeItem({
                  key: category.type,
                  removeItems: category.settingItems.map(item => item.key),
                })
              }
              className={classes.delete_button}
            >
              <Icon icon="iconamoon:close-light" />
            </CoreIconButton>
          </div>
        </div>
        <Typography className={classes.groupedSettingsDescription}>
          {category.description}
        </Typography>

        <div className={classes.groupedSettingsContent}>
          {category.settingItems.map(item => {
            // Hide image-related settings if imageSource is 'none'
            if (!shouldShowImageSettings && item.key !== 'imageSource') {
              return null
            }
            return renderSettingItem(item, true, category.title)
          })}
        </div>
      </div>
    )
  }
  return (
    <div
      className={classes.courseInfo}
      style={{
        gap: settingsAccordionOpen ? '16px' : '0px',
        transition: 'all 0.1s ease',
      }}
    >
      <div className={classes.courseInfoTitleBetween}>
        <span className={classes.sectionLabel}>
          Customization <span className={classes.optional}>(optional)</span>
        </span>
      </div>
      <div
        className={`${classes.accordionContent} ${
          settingsAccordionOpen ? 'open' : ''
        }`}
      >
        <div className={classes.root}>
          {settingList.length > 0 &&
            settingList.map(items => {
              // Check if this is an image settings group
              if (items.type === 'imageSettings') {
                return (
                  <ImageSettings
                    key={`${items.type}-${attachments.length}`}
                    classes={classes}
                    category={items}
                    selectedSettings={selectedSettings}
                    onChangeHandler={onChangeHandler}
                    removeItem={removeItem}
                    attachments={attachments}
                  />
                )
              }
              // Regular settings rendering
              return items.settingItems.map(item => {
                return renderSettingItem(item)
              })
            })}

          <div className={classes.button_container}>
            {dataFunc()
              .filter(
                item =>
                  settingList.filter(x => x.type == item.type).length == 0,
              )
              .filter(item => {
                // Hide tone and audience settings when attachments are uploaded and documentFollowing is 'preserve'
                if (shouldHideToneAndAudience()) {
                  return item.type !== 'tone' && item.type !== 'audience'
                }
                return true
              })
              .map(item => (
                <div
                  className={classes.button}
                  onClick={() => {
                    handleClickOnSettingAddButton(item)
                  }}
                >
                  <Icon icon="tabler:plus" width={14} height={14} />
                  {item.title}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(Settings)
